import { BUSINESS_TOOL_SECTIONS, BUSINESS_TOOL_SUB_SECTIONS } from "constants/businessTool";

import { concatWithDot } from "helpers/string";

import { COST_OF_SALES_FORM_NAMES } from "../../CostOfSalesForm/utils/form";
import { GENERAL_AND_ADMINISTRATIVE_FORM_NAMES } from "../../GeneralAndAdministrativeForm/utils/form";
import { R_AND_D_FORM_NAMES } from "../../RAndDForm/utils/form";
import { SALES_AND_MARKETING_FORM_NAMES } from "../../SalesAndMarketingForm/utils/form";

 const plannedExpensesPrefix = concatWithDot(
  BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN,
  BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PLANNED_EXPENSES,
);

const EXPENSE_TYPE_NAMES = {
  MONTHLY_EXPENSES: 'monthlyExpenses',
  COST_OF_EACH_UNIT: 'costOfEachUnit',
  TOTAL_EXPENSES: 'totalExpenses',
  COST_OF_SALES: 'costOfSales',
  R_AND_D: 'r&D',
  SALES_AND_MARKETING: 'salesAndMarketing',
  GENERAL_AND_ADMINISTRATIVE: 'generalAndAdministrative',
  PERSONNEL_EXPENSES: 'personnelExpenses',
} as const;

const costOfSalesPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.COST_OF_SALES);
const rAndDPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.R_AND_D);
const salesAndMarketingPrefix = concatWithDot(plannedExpensesPrefix, EXPENSE_TYPE_NAMES.SALES_AND_MARKETING);
const generalAndAdministrativePrefix = concatWithDot(
  plannedExpensesPrefix,
  EXPENSE_TYPE_NAMES.GENERAL_AND_ADMINISTRATIVE,
);

export const MONTHLY_EXPENSES_FORM_CHUNKS = {
  COST_OF_SALES_OF_PRODUCTS: 'costOfSalesOfProducts',
  R_AND_D: 'rAndD',
  SALES_AND_MARKETING: 'salesAndMarketing',
  GENERAL_AND_ADMINISTRATIVE: 'generalAndAdministrative',
  OTHER_OPERATING_EXPENSES: 'otherOperatingExpenses',
  FINANCIAL_EXPENSES: 'financialExpenses',
  NON_OPERATING_EXPENSES: 'nonOperatingExpenses',
} as const;

export const MONTHLY_EXPENSES_FORM_NAMES = {
  [MONTHLY_EXPENSES_FORM_CHUNKS.COST_OF_SALES_OF_PRODUCTS]: {
    EXPENSES: 'costOfSalesExpenses',
    REVENUE: 'costOfSalesRevenue',
    GROWTH_RATE: 'costOfSalesGrowthRate',
    TABLE_NAME: concatWithDot(costOfSalesPrefix, COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE,),
  },
  [MONTHLY_EXPENSES_FORM_CHUNKS.R_AND_D]: {
    EXPENSES: 'randDExpenses',
    REVENUE: 'randDRevenue',
    GROWTH_RATE: 'randDGrowthRate',
    TABLE_NAME: concatWithDot(rAndDPrefix, R_AND_D_FORM_NAMES.R_AND_D_TABLE),
  },
  [MONTHLY_EXPENSES_FORM_CHUNKS.SALES_AND_MARKETING]: {
    EXPENSES: 'salesAndMarketingExpenses',
    REVENUE: 'salesAndMarketingRevenue',
    GROWTH_RATE: 'salesAndMarketingGrowthRate',
    TABLE_NAME: concatWithDot(salesAndMarketingPrefix, SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE),
  },
  [MONTHLY_EXPENSES_FORM_CHUNKS.GENERAL_AND_ADMINISTRATIVE]: {
    EXPENSES: 'generalAndAdminExpenses',
    REVENUE: 'generalAndAdminRevenue',
    GROWTH_RATE: 'generalAndAdminGrowthRate',
    TABLE_NAME: concatWithDot(generalAndAdministrativePrefix,
      GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE)
  },
  [MONTHLY_EXPENSES_FORM_CHUNKS.OTHER_OPERATING_EXPENSES]: {
    EXPENSES: 'otherOperatingExpenses',
    REVENUE: 'otherOperatingRevenue',
    GROWTH_RATE: 'otherOperatingGrowthRate',
    TABLE_NAME: ''
  },
  [MONTHLY_EXPENSES_FORM_CHUNKS.FINANCIAL_EXPENSES]: {
    EXPENSES: 'financialExpenses',
    REVENUE: 'financialRevenue',
    GROWTH_RATE: 'financialGrowthRate',
    TABLE_NAME: ''
  },
  [MONTHLY_EXPENSES_FORM_CHUNKS.NON_OPERATING_EXPENSES]: {
    EXPENSES: 'nonOperatingExpenses',
    REVENUE: 'nonOperatingRevenue',
    GROWTH_RATE: 'nonOperatingGrowthRate',
    TABLE_NAME: ''
  },
} as const;

export type IMonthlyExpensesFormNames = typeof MONTHLY_EXPENSES_FORM_NAMES;

export interface IMonthlyExpensesFormValuesType {
  [key: string]: string;
}

export const monthlyExpensesFormInitialValues: IMonthlyExpensesFormValuesType = Object.values(
  MONTHLY_EXPENSES_FORM_NAMES,
).reduce((acc, chunk) => {
  acc[chunk.EXPENSES] = '';
  acc[chunk.REVENUE] = '';
  acc[chunk.GROWTH_RATE] = '';
  return acc;
}, {} as IMonthlyExpensesFormValuesType);

export const MONTHLY_EXPENSES_FORM_TYPES = {
  COST_OF_SALES_PRODUCTS: 'COST_OF_SALES_PRODUCTS',
  GENERAL_AND_ADMINISTRATIVE: 'GENERAL_AND_ADMINISTRATIVE',
  SALES_AND_MARKETING: 'SALES_AND_MARKETING',
  RESEARCH_AND_DEVELOPMENT: 'RESEARCH_AND_DEVELOPMENT',
  OTHER_OPERATING_EXPENSES: 'OTHER_OPERATING_EXPENSES',
  FINANCIAL_EXPENSES: 'FINANCIAL_EXPENSES',
  NON_OPERATING_EXPENSES: 'NON_OPERATING_EXPENSES',
} as const;

export type monthlyExpansesFormType = {
  [K in keyof IMonthlyExpensesFormNames as IMonthlyExpensesFormNames[K]['EXPENSES'] | IMonthlyExpensesFormNames[K]['GROWTH_RATE']]: number;
}
